import axios from 'axios';

/**
 * Shared axios instance for the TalentTech API.
 *
 * - Base URL comes from REACT_APP_API_URL (see .env.example); defaults to the local backend.
 * - Attaches the JWT access token on every request.
 * - On a 401, tries a one-shot refresh with the stored refresh token, then replays the request.
 * - Unwraps the backend's ApiResponse envelope: responses resolve to `response.data.data`
 *   and errors reject with an Error carrying `.message` / `.errors` / `.status`.
 */

export const API_BASE_URL =
  process.env.REACT_APP_API_URL || 'http://localhost:8080/api/v1';

/** Backend origin (no /api/v1) — used for the SockJS chat endpoint and upload URLs. */
export const BACKEND_ORIGIN = API_BASE_URL.replace(/\/api\/v1\/?$/, '');
export const WS_URL = `${BACKEND_ORIGIN}/ws`;

export const TOKEN_KEY = 'tt_access_token';
export const REFRESH_KEY = 'tt_refresh_token';
export const USER_KEY = 'tt_user';

export const tokenStore = {
  getAccess: () => localStorage.getItem(TOKEN_KEY),
  getRefresh: () => localStorage.getItem(REFRESH_KEY),
  getUser: () => {
    try {
      return JSON.parse(localStorage.getItem(USER_KEY));
    } catch {
      return null;
    }
  },
  set: ({ token, refreshToken, user }) => {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    if (refreshToken) localStorage.setItem(REFRESH_KEY, refreshToken);
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
  },
  clear: () => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
};

const axiosClient = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
});

axiosClient.interceptors.request.use((config) => {
  const token = tokenStore.getAccess();
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshing = null;

async function refreshAccessToken() {
  const refreshToken = tokenStore.getRefresh();
  if (!refreshToken) throw new Error('No refresh token');
  // Bare axios so we don't recurse through this interceptor.
  const { data } = await axios.post(`${API_BASE_URL}/auth/refresh`, { refreshToken });
  const payload = data.data;
  tokenStore.set({
    token: payload.token,
    refreshToken: payload.refreshToken,
    user: payload.user,
  });
  return payload.token;
}

axiosClient.interceptors.response.use(
  (response) => (response.data && 'data' in response.data ? response.data.data : response.data),
  async (error) => {
    const { response, config } = error;

    if (response && response.status === 401 && !config._retried && tokenStore.getRefresh()) {
      config._retried = true;
      try {
        refreshing = refreshing || refreshAccessToken();
        const newToken = await refreshing;
        refreshing = null;
        config.headers.Authorization = `Bearer ${newToken}`;
        return axiosClient(config);
      } catch (e) {
        refreshing = null;
        tokenStore.clear();
        if (typeof window !== 'undefined') window.dispatchEvent(new Event('tt:logout'));
      }
    }

    const apiError = new Error(
      response?.data?.message || error.message || 'Request failed'
    );
    apiError.status = response?.status;
    apiError.errors = response?.data?.errors;
    return Promise.reject(apiError);
  }
);

export default axiosClient;
